// The `rounds-http` client (rounds-slice tasks 8.8-8.10): the production implementation
// of BOTH rounds-mode client seams —
//
//   * `ColonyRunController` (`ui/colony/run-controller.ts`) — submit a whole-colony
//     candidate, await its single completion signal, list this sandbox's runs;
//   * `TraceReader` (`net/rounds-review-source.ts`) — open a run's published trace for
//     the review screen.
//
// It is a `fetch` client and nothing more, in the `lobby-http.ts` / `replay-http.ts`
// mould: a trace moves as files and byte ranges, so none of this touches `waggle-proto`'s
// WebSocket wire, its LOD tiers or its codec — there is no protocol version bump here.
//
// Two shapes deserve a note:
//
//   * **Typed refusals are values, not exceptions.** Every named refusal the surface can
//     return (`admission`, `compile_failed`, `unauthorized`, …) becomes a `RunRefusal` the
//     Colony screen turns into a sentence. Only a *transport* failure is unusual enough to
//     surface as `{kind: 'unavailable'}` — and even that is a value, never a throw, because
//     a silent no-op is exactly what the spec forbids.
//   * **The detail tier is fetched by window.** The surface serves detail by tick range
//     through the O(1) index (**A4**) and serves **no** index route, so the client cannot
//     rebuild a whole `DecodedTrace` over HTTP the way `openTrace` does from local files.
//     It does not need to: `ReviewTrace` was designed around exactly this residency
//     question, so a window arrives, its records are decoded once, and every tick in it
//     becomes resident. Ticks not yet resident render coarse — a real view, not a stall.

import type {
  ColonyRunController,
  RunCandidate,
  RunDiagnostic,
  RunOutcome,
  RunRefusal,
  RunSubmission,
} from '../ui/colony/run-controller';
import type { ColonyMeta, DocumentDiagnostic, PromptStatus, PromptStatusKind, RejectReason, TypeMeta } from './wire/messages';
import { ChatSseClient } from './chat-sse';
import type {
  ReviewRun,
  ReviewTrace,
  TraceCorruptionReport,
  TraceLoad,
  TraceLocator,
  TraceReader,
} from './rounds-review-source';
import {
  SpanReplayer,
  TraceCorruptionError,
  decodeCoarse,
  parseTraceManifestResponse,
  spanAnchor,
  unframe,
  type CoarsePreamble,
  type CoarseSample,
  type DetailRecord,
  type EvictedSpan,
  type TraceComponentName,
  type TraceManifest,
  type TraceManifestResponse,
  type TraceRetention,
} from './rounds-trace';
import { normalizeEvictedSpans } from './rounds-review-source';

/** A run's status string, exactly as `rounds-http`'s `RunDto` spells it. */
export type RunStatus =
  | 'in_flight'
  | 'completed'
  | 'cancelled'
  | 'simulation_failed'
  | 'trace_io_failed';

/** One colony's end-of-run figures, from a run's `evidence` block. */
export interface RunColonyScore {
  colony: number;
  livingCount: number;
  /** Nectar in the colony's stores at the end of the run. */
  pool: number;
}

/** The `evidence` block a terminal `RunDto` carries — the batch service's own record of how the
 * run went, which is where the review rail's supporting stats come from rather than being
 * invented client-side. */
export interface RunEvidenceDto {
  colonyCount?: number;
  scores?: RunColonyScore[];
}

/** `rounds-http`'s `RunDto`, camelCase on the wire already. */
export interface RunDto {
  runId: number;
  status: RunStatus;
  ticksObserved: number;
  terminalTick: number | null;
  score: number | null;
  stateHash: string | null;
  detail: string | null;
  evidence?: RunEvidenceDto | null;
  /**
   * The document version ordinal the run was **submitted from** — the linkage that lets a run,
   * its score and its trace be attributed to the version whose programs produced it. Without it
   * a run and a version are unrelatable and a version-first presentation of runs cannot be built.
   *
   * `null` means **unattributed**: the run's durable record predates the field. It is deliberately
   * not `0` — no sandbox ever issues ordinal 0 — so a consumer must group a `null` under no
   * version rather than under a version that does not exist. The key is always on the wire (the
   * server serializes the absent case as `null` rather than omitting it); a consumer reading a
   * hand-built or older payload should still normalize with `?? null`.
   */
  documentVersion: number | null;
}

/** One recorded whole-document snapshot as `GET /rounds/{sandbox}/versions` lists it.
 *
 * **No `text`.** A history of whole documents is unbounded, and the only moment the text is
 * needed is activation, which returns it — so a selector renders from this and asks for bytes
 * only when a version is actually chosen. */
export interface DocumentVersionDto {
  ordinal: number;
  summary: string;
  /** `'agent'` for a version a chat turn wrote, `'manual'` for the player's own write. */
  origin: 'agent' | 'manual';
  /** Whether that snapshot compiles — what marks a version the Run gate would refuse. */
  validates: boolean;
  /**
   * The two-or-three-word name the agent chose from the player's prompt, or `null` when it has none
   * (`home-and-single-player`). It is what lets a player tell their own attempts apart at a glance; its
   * absence falls back to the stable, never-reused ordinal.
   */
  name: string | null;
  /**
   * The player prompt that produced this version, **denormalised onto the version row** at commit.
   *
   * On the wire rather than looked up because the alternative was the chat transcript tables, which sit
   * on the delete-with-the-match side of an existing retention split — and because it makes disclosure
   * an explicit act at the moment of commit rather than a join a reader performs later. `null` for a
   * version no prompt produced.
   */
  prompt: string | null;
  /** Present only when `origin === 'agent'`: the turn that caused the version. */
  threadId?: string;
  messageId?: string;
}

/** A sandbox's whole version history plus the head named outright.
 *
 * The head is carried rather than inferred because activation **relocates** it instead of
 * appending: after restoring v1 of a five-version history the working document is v1 while the
 * highest ordinal is still 5, so `max(ordinal)` is simply the wrong answer. */
export interface VersionListDto {
  headOrdinal: number;
  versions: DocumentVersionDto[];
}

/**
 * The outcome of one version activation.
 *
 * Mirrors {@link HandEditDispatch}'s discriminated shape — and reuses its {@link HandEditResult}
 * payload verbatim — because the server answers activation in the hand-edit routes' own body:
 * one decoder, one store-apply path, whichever of the three routes moved the document.
 *
 * The two refusals get named kinds for the same reason `turn_in_progress` did on a hand edit: a
 * version that is not there and a turn holding the sandbox's slot are different things to say to
 * a player, and neither is a generic failure.
 */
export type ActivateDispatch =
  | { kind: 'applied'; result: HandEditResult }
  | { kind: 'unknownVersion' }
  | { kind: 'turnInFlight' }
  | { kind: 'failed'; detail?: string };

/**
 * What became of a submitted Beekeeper prompt at the moment it was submitted (not what the
 * generation eventually produced — that arrives as {@link PromptStatus} events).
 */
export type PromptDispatch =
  | { kind: 'queued'; promptId: number; fromSeq: number }
  | { kind: 'refused'; reason: RejectReason };

/** One page of the sandbox's generation status log, plus the player's advisory cooldown. */
export interface PromptStatusPage {
  /** The cursor to resume from — the log's latest sequence number. */
  seq: number;
  /**
   * Milliseconds left on the player's **advisory** prompt cooldown, `0` when clear.
   *
   * The server sends a remainder rather than a deadline because its `Instant` means nothing here
   * (`rounds-http`'s own note). This is the only wall-clock quantity the sandbox phase carries, and
   * it exists because a sandbox has **no running clock**: every status is tick-stamped with the last
   * completed run's terminal tick, which does not advance while a player edits, so nothing on the
   * wire could otherwise tell a client that a 60-second cooldown has elapsed.
   */
  cooldownRemainingMs: number;
  /** The statuses after the requested cursor, in order. */
  events: readonly PromptStatus[];
}

/**
 * What a successful `POST .../types/{typeName}/source` or `POST .../document` answers
 * (beekeeper-agent-chat, CONTRACT.md §6) — the sandbox's document-mode `ColonyMeta` fields in one
 * shot, plus the new document version ordinal the save just cut.
 *
 * Deliberately **not** a compile refusal (design D-2): the route accepts a base that does not
 * parse and a result that still does not parse, so `documentValid: false` here is a normal,
 * successful answer — Run is the compile gate, not a hand-edit save. A `HandEditDispatch` therefore
 * only ever carries a `RejectReason` for something OTHER than "did not compile."
 *
 * **`documentValid` and `documentDiagnostics` are optional, and absence is a third state — not a
 * spelling of `false`.** A practice sandbox performs no compile at all (it happens on the device),
 * so its hand-edit and activate routes answer with no `documentValid` key at all: "unknown",
 * never "does not parse". Coercing that absence to `false` (`dto['documentValid'] === true`, the
 * shape this type used to have) made a document the device would compile fine present as broken.
 * The two rounds readers that consume this (`SandboxScreen.tsx`'s `documentValid` and
 * `sandbox-coordinator.ts`'s `transportInputFor`) already end in `?? true` for exactly this case.
 */
export interface HandEditResult {
  version: number;
  document: string;
  documentValid?: boolean;
  documentDiagnostics?: DocumentDiagnostic[];
  turnInFlight: boolean;
}

/** The outcome of one rounds hand-edit save (per-type or whole-document). */
export type HandEditDispatch =
  | { kind: 'applied'; result: HandEditResult }
  | { kind: 'refused'; reason: RejectReason };

// ---------------------------------------------------------------------------------------
// The tournament-read family (`rounds-client` **D1**/**D2**)
//
// Three routes, all authorized by the **session** bearer token this client already carries —
// `GET /rounds/tournament`, `GET /rounds/tournament/standings` and
// `POST /rounds/tournament/selection`. They are tournament facts fetched with the sandbox
// client's credential, which is why they live on this class rather than on
// {@link TournamentReadClient}: that one holds the 64-hex *tournament* credential and addresses
// graded traces by `(tournament, round, colony)`. The two never mix (**C18**).
// ---------------------------------------------------------------------------------------

/**
 * The caller's own submission for the current round.
 *
 * `provenance` is nullable on the wire because a submission may exist before the transition that
 * classifies it; the two `defaulted_*` values are the only ones **G9**'s notice binds to.
 */
export interface SubmissionReadDto {
  provenance: string | null;
  candidateRef: string;
}

/**
 * `GET /rounds/tournament` — the whole phase surface in one read.
 *
 * **`phase` and `memberKind` are deliberately bare `string`s, not unions.** `client-rounds-phase`
 * requires the client to render whatever the tournament reports and to enumerate no phase machine
 * of its own: a union here would turn an unrecognized phase into a *parse* failure, when the
 * specified behaviour is an inert waiting state. The vocabulary the server ships today is
 * `sandbox | simulate | review | standings | cancelling | cancelled` and
 * `host | competitor | spectator`, and the client classifies rather than validates.
 */
export interface TournamentReadDto {
  tournamentId: string;
  roundsTotal: number;
  currentRound: number;
  phase: string;
  membershipClosed: boolean;
  memberKind: string;
  joinOrdinal: number;
  /** The caller's colony for the round, or `null` before membership closed / for a spectator. */
  colonyId: number | null;
  displayName: string;
  /** The caller's live sandbox scope, or `null` for a spectator. */
  sandbox: string | null;
  /** **A remainder, never a deadline** — seconds left in this phase, `null` for a phase that
   * carries none (the simulating phase, whose end is reported). Never negative. */
  remainingS: number | null;
  submissionOpen: boolean;
  submission: SubmissionReadDto | null;
  /** Competing members who have not marked a submission; `null` outside the editing phase. */
  outstanding: number | null;
  /** How many members have **joined** — present only during the join window
   * (`tournament-big-screen` **D14**'s `joined` field), `null` in every other phase. A waiting
   * phone reads this to confirm the room is filling without polling the host-only members read. */
  joined: number | null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Parses `GET /rounds/tournament`'s body, or `null` when it is not a tournament read at all.
 *
 * Nothing is invented and nothing is clamped away except a negative remainder, which the server
 * states it never sends — floored at zero here so a countdown can never start below zero and
 * render a negative deadline if that ever stops being true.
 */
export function tournamentReadFromDto(json: unknown): TournamentReadDto | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    return null;
  }
  const named = json as Record<string, unknown>;
  const phase = named['phase'];
  const currentRound = named['currentRound'];
  const roundsTotal = named['roundsTotal'];
  if (typeof phase !== 'string' || typeof currentRound !== 'number' || typeof roundsTotal !== 'number') {
    return null;
  }
  const rawSubmission = named['submission'];
  let submission: SubmissionReadDto | null = null;
  if (rawSubmission !== null && typeof rawSubmission === 'object' && !Array.isArray(rawSubmission)) {
    const row = rawSubmission as Record<string, unknown>;
    const candidateRef = row['candidateRef'];
    if (typeof candidateRef === 'string') {
      submission = {
        provenance: typeof row['provenance'] === 'string' ? row['provenance'] : null,
        candidateRef,
      };
    }
  }
  const remainingS = optionalNumber(named['remainingS']);
  return {
    tournamentId: typeof named['tournamentId'] === 'string' ? named['tournamentId'] : '',
    roundsTotal,
    currentRound,
    phase,
    membershipClosed: named['membershipClosed'] === true,
    memberKind: typeof named['memberKind'] === 'string' ? named['memberKind'] : '',
    joinOrdinal: optionalNumber(named['joinOrdinal']) ?? 0,
    colonyId: optionalNumber(named['colonyId']),
    displayName: typeof named['displayName'] === 'string' ? named['displayName'] : '',
    sandbox: typeof named['sandbox'] === 'string' ? named['sandbox'] : null,
    remainingS: remainingS === null ? null : Math.max(0, remainingS),
    submissionOpen: named['submissionOpen'] === true,
    submission,
    outstanding: optionalNumber(named['outstanding']),
    joined: optionalNumber(named['joined']),
  };
}

/** One member's score in one graded round. Only graded rounds appear — an ungraded round has no
 * row at all, which is what keeps **G2**'s denominator the server's rather than the client's. */
export interface RoundScoreDto {
  roundNo: number;
  score: number;
}

/** One row of `GET /rounds/tournament/standings`.
 *
 * `mean` and `rank` are nullable together: a spectator competes in nothing, so it has neither,
 * and before the first graded round nobody does. There is deliberately **no `colonyId`** — the
 * colony↔member mapping is derived from `joinOrdinal` (**D4**) and its absence here is recorded
 * as a deferral rather than worked around. */
export interface MemberStandingDto {
  player: number;
  displayName: string;
  joinOrdinal: number;
  memberKind: string;
  roundScores: RoundScoreDto[];
  mean: number | null;
  rank: number | null;
}

/** `GET /rounds/tournament/standings` — the whole board, readable in every phase. */
export interface StandingsDto {
  gradedRounds: number;
  members: MemberStandingDto[];
}

/** Why the tournament refused to record a selection. Every arm is a *typed* server refusal
 * except `unavailable`, which is the transport failing to deliver the question at all. */
export type SelectionRefusal =
  | 'submission_closed'
  | 'tournament_cancelled'
  | 'spectator'
  | 'unknown_candidate'
  | 'unauthorized'
  | 'unavailable';

/** The outcome of one `POST /rounds/tournament/selection`. A refusal is a **value**, matching
 * every other write on this client, so the affordance maps it to copy rather than catching. */
export type SelectionOutcome =
  | { kind: 'recorded'; provenance: string; candidateRef: string }
  | { kind: 'refused'; reason: SelectionRefusal };

/**
 * Maps a non-2xx selection response onto its typed reason.
 *
 * The body's `error` is preferred over the status, because two of the five refusals share a
 * status (`403` covers both `spectator` and `unauthorized`) and the affordance says different
 * things about them. An unrecognized body becomes `unavailable` rather than being reported as a
 * refusal the server did not give.
 */
function selectionRefusalFrom(status: number, body: unknown): SelectionRefusal {
  const named = (body ?? {}) as Record<string, unknown>;
  const error = typeof named['error'] === 'string' ? named['error'] : null;
  if (
    error === 'submission_closed' ||
    error === 'tournament_cancelled' ||
    error === 'spectator' ||
    error === 'unknown_candidate' ||
    error === 'unauthorized'
  ) {
    return error;
  }
  if (status === 401 || status === 403) {
    return 'unauthorized';
  }
  return 'unavailable';
}

/**
 * The **narrow** tournament seam every rounds surface above the net layer consumes — the phase
 * channel, the standings source and the selection affordance.
 *
 * It exists so nothing in `app/rounds/` has to know *which credential authorized what*: the
 * composition root decides that once (**D2**) and hands down three operations.
 * {@link RoundsHttpClient} satisfies it; `test/fake-rounds-tournament.ts` satisfies it without a
 * server. Deliberately three methods and no more — there is no route here that could reach
 * another member's program source.
 */
export interface RoundsTournamentTransport {
  /** The phase read. `null` when it could not be read at all — never a fabricated phase. */
  fetchTournament(): Promise<TournamentReadDto | null>;
  /** The standings read. `null` when it could not be read at all. */
  fetchStandings(): Promise<StandingsDto | null>;
  /** Marks `candidateId` as the caller's submission for the current round. */
  markSelection(candidateId: number): Promise<SelectionOutcome>;
}

export interface RoundsHttpConfig {
  /** e.g. `http://127.0.0.1:8080` — no trailing slash required. */
  baseUrl: string;
  /** The sandbox's own scope id, `sandbox-r{round}-p{player}`. */
  scope: string;
  /** The bearer credential, hex, from the session the player minted. */
  token: string;
  /**
   * Re-mints the session bearer from the membership's durable credential. See
   * {@link BearerRenewal} for why the answer has three arms rather than two.
   *
   * **The bearer is the perishable half of a membership.** `SessionRegistry` is in-memory
   * (`waggle-server/src/session.rs`) and a rounds mint persists no session row, so *any* server
   * restart disowns every token in the room while the 64-hex tournament credential — the only
   * durable way back into a membership — stays valid. Without this the client presents a token
   * the server has forgotten on every poll, for ever: E2E round-3 finding C4 measured 180+
   * consecutive refusals on a player tab that a manual reload fixed instantly, because a reload
   * is the only thing that presents the credential a second time.
   *
   * Optional: a client given none simply reports the refusal, exactly as it did before.
   */
  renewToken?: () => Promise<BearerRenewal>;
  fetchImpl?: typeof fetch;
  /**
   * How many ticks a single detail fetch covers. Bounded server-side by the reader's own
   * `MAX_DETAIL_RANGE_TICKS` (600) — no client can widen it. The default is one second of
   * simulation, which keeps a fetch small while making a scrub land inside a window most
   * of the time.
   */
  detailWindowTicks?: number;
  /** See {@link DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS}. */
  maxResidentDetailWindows?: number;
}

const DEFAULT_DETAIL_WINDOW_TICKS = 15;

/**
 * The most detail-tier fetch windows {@link buildReviewTrace} keeps decoded and resident at
 * once, oldest evicted first (E2E round-3 finding D: a projector rehearsal's presenter tab
 * hard-crashed, "Target crashed", ~20 s into 16× spectate playback of the graded 150-colony,
 * 30-minute trace).
 *
 * The prefetch lead in `RoundsReview.tsx` scales with playback speed (up to 16×), so a
 * sustained fast-forward over a long trace requests hundreds of windows a minute — and every
 * one of them used to stay in `resident` for the trace's whole lifetime, because nothing ever
 * called `.delete()`. A synthetic-but-representative measurement (30 colonies × 60 bees × 40
 * cells/colony × 1,200 patches per tick — a mid-collapse point of the 150-colony design
 * ceiling, `openspec/changes/rounds-review-detail-cache-eviction/evidence/resident-cache-growth.mjs`)
 * showed that Map growing **~9.1 MB of live heap per window** with no ceiling: 400 windows (the
 * ~20 s / 320-window scale of the reported crash) reached 3.66 GB and climbing. The same shape
 * capped at this many windows (cap × per-window bytes — machine-independent, so the absolute
 * MB below is this measuring machine's, not a portable constant) plateaus at a constant **~368
 * MB** of live heap forever, verified out to 4,000 windows with forced GC confirming no residual
 * growth — more than the whole 30-minute trace's worth, six times over
 * (`openspec/changes/rounds-review-detail-cache-eviction/evidence/resident-cache-bounded-gc.mjs`).
 *
 * 40 windows of the default 15-tick window is ~40 s of sim time held resident at once: past
 * that, a backward scrub just re-fetches — cheap, and identical to a position's first visit —
 * rather than the whole trace accumulating in memory. Scrubbing near the playhead, the common
 * diagnosis case, never notices the cap.
 *
 * ponytail: a fixed window count, not adaptive to a run's actual colony/bee count — the
 * measurement above is a representative worst case, not a per-run budget. Revisit if a future
 * profiling pass finds either headroom to raise it or a shape that still crowds it out.
 */
export const DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS = 40;

/** A transport failure (offline, DNS, a body that is not the JSON it claimed). */
function unavailable(message: string): RunRefusal {
  return { kind: 'unavailable', message };
}

/** Maps one typed refusal body onto a `RunRefusal`. */
function refusalFrom(status: number, body: unknown): RunRefusal {
  const named = (body ?? {}) as Record<string, unknown>;
  const error = typeof named['error'] === 'string' ? (named['error'] as string) : null;
  if (error === 'admission') {
    return {
      kind: 'admission',
      scope: named['scope'] === 'server' ? 'server' : 'player',
      limit: typeof named['limit'] === 'number' ? named['limit'] : 0,
      inFlight: typeof named['inFlight'] === 'number' ? named['inFlight'] : 0,
    };
  }
  if (error === 'compile_failed') {
    const raw = Array.isArray(named['diagnostics']) ? (named['diagnostics'] as unknown[]) : [];
    const diagnostics: RunDiagnostic[] = raw.map((d) => {
      const row = (d ?? {}) as Record<string, unknown>;
      return {
        typeName: typeof row['typeName'] === 'string' ? (row['typeName'] as string) : 'a bee type',
        message: typeof row['message'] === 'string' ? (row['message'] as string) : 'did not compile',
      };
    });
    return { kind: 'compileFailed', diagnostics };
  }
  if (error === 'unauthorized' || status === 401 || status === 403) {
    return { kind: 'unauthorized' };
  }
  // Design D7: one compare-and-swap on the document ordinal, replacing the per-type base-version
  // checks — named so the screen can say "the document moved" rather than an anonymous failure.
  if (error === 'stale') {
    return { kind: 'stale' };
  }
  if (error !== null) {
    return { kind: 'invalid', error };
  }
  return unavailable(`the server answered ${status}`);
}

/**
 * Maps a **prompt** refusal onto the live wire's own {@link RejectReason} vocabulary.
 *
 * The pairings are deliberate, and two of them deserve naming:
 *
 *   * `cooling_down` and `generation_in_progress` both become `rateLimited` — "wait, then try
 *     again" is what each one means and what the box's copy already says. `generation_in_progress`
 *     should be near-unreachable (the screen's own pending state gates the same thing), and it
 *     matters only across types, where the sandbox's single generation slot is the real gate.
 *   * everything unnamed becomes `invalid` ("that didn't go through — try again"), which is the
 *     right thing to say about an unrecognized refusal *and* about a dead socket, rather than
 *     inventing a reason the server did not give.
 */
function promptRefusalFrom(status: number, body: Record<string, unknown> | null): RejectReason {
  const error = typeof body?.['error'] === 'string' ? (body['error'] as string) : null;
  if (error === 'unauthorized' || status === 401 || status === 403) {
    return { kind: 'unauthorized' };
  }
  if (error === 'cooling_down' || error === 'generation_in_progress') {
    return { kind: 'rateLimited' };
  }
  if (error === 'stale') {
    return { kind: 'stale' };
  }
  return { kind: 'invalid' };
}

/**
 * Maps a **hand-edit** refusal (`POST .../types/{typeName}/source` or `POST .../document`,
 * CONTRACT.md §6) onto {@link RejectReason} — the same "existing mutationRejection surface"
 * `client-colony`'s live-mode HandEdit already reconciles through, so the Code tab's Save needs no
 * second code path for rounds mode.
 *
 * `turn_in_progress` gets its own named kind (`turnInProgress`) rather than folding into
 * `rateLimited`: a running Beekeeper turn is not a cooldown, and the Code tab reopens edit mode on
 * it exactly as it already does for `stale`, never discarding the draft. Everything unnamed — a
 * missing sandbox (`not_found`), a malformed body (`invalid_body`/`invalid_source`), an unknown
 * type (`unknown_type`, per-type route only) — becomes `invalid`, matching `promptRefusalFrom`'s
 * own catch-all rather than inventing a reason the server did not give. There is deliberately no
 * `compileFailed` mapping here: design D-2 means a save is never refused for not compiling — a
 * 200 with `documentValid: false` is what that looks like instead.
 */
function handEditRefusalFrom(status: number, body: Record<string, unknown> | null): RejectReason {
  const error = typeof body?.['error'] === 'string' ? (body['error'] as string) : null;
  if (error === 'unauthorized' || status === 401 || status === 403) {
    return { kind: 'unauthorized' };
  }
  if (error === 'stale') {
    return { kind: 'stale' };
  }
  if (error === 'turn_in_progress') {
    return { kind: 'turnInProgress' };
  }
  return { kind: 'invalid' };
}

/**
 * Maps an **activation** refusal onto {@link ActivateDispatch}'s named kinds.
 *
 * Keyed on the server's own `error` string first and its status second, deliberately: the shipped
 * `SandboxError::UnknownVersion` mapping answers `422` while the settled version contract wrote
 * `404`, and either is a perfectly good way to say "there is no such version". Binding to the
 * machine-readable reason means the refusal stays *named* — the difference between "that version
 * is gone" and an anonymous failure the screen can only apologise for — whichever status carries
 * it.
 *
 * Everything unnamed keeps its `error` string as `detail`, which is more than the hand-edit
 * mapping does, because there is no third named kind to fold it into and a screen that can say
 * `not_found` is better than one that can only say something went wrong.
 */
function activateRefusalFrom(status: number, body: Record<string, unknown> | null): ActivateDispatch {
  const error = typeof body?.['error'] === 'string' ? (body['error'] as string) : null;
  if (error === 'unknown_version') {
    return { kind: 'unknownVersion' };
  }
  if (error === 'turn_in_progress') {
    return { kind: 'turnInFlight' };
  }
  return error === null ? { kind: 'failed', detail: `http_${status}` } : { kind: 'failed', detail: error };
}

/**
 * Decodes one history row, or `null` if it is not one.
 *
 * Malformed rows are **dropped, not defaulted** — the same discipline `documentDiagnosticsFrom`
 * and `promptStatusPageFrom` already keep. A version selector is a list of things a player can
 * restore, so a row invented from a garbled answer would be a control that activates something
 * nobody recorded. `origin` is treated as `'manual'` only when it says so: an unrecognized origin
 * is a row this client does not understand, not a player write.
 */
function documentVersionFrom(entry: unknown): DocumentVersionDto | null {
  if (entry === null || typeof entry !== 'object') {
    return null;
  }
  const row = entry as Record<string, unknown>;
  const origin = row['origin'];
  if (typeof row['ordinal'] !== 'number' || (origin !== 'agent' && origin !== 'manual')) {
    return null;
  }
  const version: DocumentVersionDto = {
    ordinal: row['ordinal'] as number,
    summary: typeof row['summary'] === 'string' ? (row['summary'] as string) : '',
    origin,
    validates: row['validates'] === true,
    // **`null`, never `''`.** An empty name would render as a blank headline and an empty prompt as a
    // blank quotation — two different and false facts. Absent means absent, and the display falls back
    // to the ordinal and to no subtitle respectively.
    name: typeof row['name'] === 'string' && row['name'] !== '' ? (row['name'] as string) : null,
    prompt: typeof row['prompt'] === 'string' && row['prompt'] !== '' ? (row['prompt'] as string) : null,
  };
  // Provenance travels only with an agent row, exactly as the surface sends it — a `threadId` on
  // a manual version would be a field the caller has to disbelieve.
  if (origin === 'agent') {
    if (typeof row['threadId'] === 'string') {
      version.threadId = row['threadId'];
    }
    if (typeof row['messageId'] === 'string') {
      version.messageId = row['messageId'];
    }
  }
  return version;
}

/**
 * Decodes a successful hand-edit save's body. `version`/`document`/`turnInFlight` degrade to a
 * safe, honest default rather than inventing certainty from a malformed answer (`0`/`''`/`false`).
 *
 * **`documentValid`/`documentDiagnostics` are set only when the DTO actually carries them** —
 * mirroring `documentFieldsFromDto`'s own narrowing below, which parses the very same two fields
 * for `GET /colony`. A practice sandbox's routes never send `documentValid` at all (no compile
 * happens server-side there), and that absence must reach the caller as absence: coercing a
 * missing key to `false` would report a document the device compiles fine as broken. Both readers
 * that consume this (`SandboxScreen.tsx`, `sandbox-coordinator.ts`'s `transportInputFor`) already
 * treat an absent value as "assume it compiles" (`?? true`).
 */
function handEditResultFrom(json: unknown): HandEditResult {
  const dto = (json ?? {}) as Record<string, unknown>;
  const result: HandEditResult = {
    version: typeof dto['version'] === 'number' ? (dto['version'] as number) : 0,
    document: typeof dto['document'] === 'string' ? (dto['document'] as string) : '',
    turnInFlight: dto['turnInFlight'] === true,
  };
  if (typeof dto['documentValid'] === 'boolean') {
    result.documentValid = dto['documentValid'];
  }
  const rawDiagnostics = dto['documentDiagnostics'];
  if (Array.isArray(rawDiagnostics)) {
    result.documentDiagnostics = documentDiagnosticsFrom(rawDiagnostics);
  }
  return result;
}

/**
 * Decodes the status envelope. A malformed page is **not** a status: it degrades to "nothing new,
 * cooldown unknown-so-clear" at the caller's own cursor, so a garbled answer can neither invent a
 * status nor rewind the cursor and re-apply history.
 */
function promptStatusPageFrom(json: unknown, afterSeq: number): PromptStatusPage {
  const dto = (json ?? {}) as Record<string, unknown>;
  const seq = typeof dto['seq'] === 'number' ? (dto['seq'] as number) : afterSeq;
  const cooldownRemainingMs =
    typeof dto['cooldownRemainingMs'] === 'number'
      ? Math.max(0, dto['cooldownRemainingMs'] as number)
      : 0;
  const raw = Array.isArray(dto['events']) ? (dto['events'] as unknown[]) : [];
  const events: PromptStatus[] = [];
  for (const entry of raw) {
    const status = promptStatusFrom(entry);
    if (status !== null) {
      events.push(status);
    }
  }
  return { seq, cooldownRemainingMs, events };
}

/** One status event → the store's `PromptStatus`, or `null` if it is not one. */
function promptStatusFrom(entry: unknown): PromptStatus | null {
  if (entry === null || typeof entry !== 'object') {
    return null;
  }
  const row = entry as Record<string, unknown>;
  if (typeof row['typeId'] !== 'number') {
    return null;
  }
  const kind = promptStatusKindFrom(row);
  if (kind === null) {
    return null;
  }
  return {
    // A sandbox stamps every status with the last completed run's terminal tick (`0` before the
    // first run), never a clock reading — carried through unchanged.
    tick: typeof row['tick'] === 'number' ? (row['tick'] as number) : 0,
    typeId: row['typeId'] as number,
    status: kind,
  };
}

function promptStatusKindFrom(row: Record<string, unknown>): PromptStatusKind | null {
  switch (row['kind']) {
    case 'generating':
      return { kind: 'generating' };
    case 'repairing':
      return { kind: 'repairing', attempt: typeof row['attempt'] === 'number' ? (row['attempt'] as number) : 1 };
    case 'accepted':
      return { kind: 'accepted', version: typeof row['version'] === 'number' ? (row['version'] as number) : 1 };
    case 'stale':
      return { kind: 'stale' };
    case 'failed': {
      const raw = Array.isArray(row['diagnostics']) ? (row['diagnostics'] as unknown[]) : [];
      return { kind: 'failed', diagnostics: raw.map((d) => String(d)) };
    }
    default:
      return null;
  }
}

interface RawResponse {
  status: number;
  ok: boolean;
  bytes: Uint8Array;
}

/** Sends one bearer-authorized request and buffers the whole response. Standalone (not an
 * instance method) so both {@link RoundsHttpClient} (the own-sandbox family) and
 * {@link TournamentReadClient} (the read-only tournament-read family, **H6′**) share one
 * implementation rather than two that could drift. */
async function sendRequest(
  fetchImpl: typeof fetch,
  token: string,
  method: string,
  url: string,
  body?: unknown,
): Promise<RawResponse> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetchImpl(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const buffer = await response.arrayBuffer();
  return { status: response.status, ok: response.ok, bytes: new Uint8Array(buffer) };
}

/**
 * Whether a refusal is the one that means "**this bearer is not one I know**" rather than "you may
 * not do that": `rounds_http`'s `unauthorized()`, which is what an authorized route answers for a
 * token the in-memory `SessionRegistry` cannot resolve.
 *
 * Narrow on purpose. The route family also refuses `spectator`, `sandbox_sealed`, `not_admitting`
 * and friends under their own names, and none of those is fixed by a fresh bearer — renewing on a
 * bare status would mint a session per poll for a refusal that is never going to change.
 */
function bearerDisowned(raw: RawResponse): boolean {
  const body = jsonOf(raw) as Record<string, unknown> | null;
  return isBearerDisowned(raw.status, body);
}

/** {@link bearerDisowned} over a status and an already-parsed body, for a caller (the host client)
 * that holds a `Response` rather than this module's buffered shape. */
export function isBearerDisowned(status: number, body: unknown): boolean {
  if (status !== 401 && status !== 403) {
    return false;
  }
  return (body as Record<string, unknown> | null)?.['error'] === 'unauthorized';
}

/**
 * What one attempt to re-mint a bearer produced.
 *
 * **Three arms, not two**, and the third is what keeps the recovery from being flaky. A renewal
 * that could not be *decided* — the mint never answered, or answered a transient failure — must be
 * retried on the next request, or a network blip landing in the same second as a server restart
 * would permanently disable the very recovery this exists for. A renewal that was *definitively*
 * refused must never be retried, or a membership that has genuinely ended (revoked, or belonging to
 * a tournament that has been succeeded) would mint one dead session per poll for ever. Collapsing
 * the two into one `null` gets one of those wrong whichever way it is read.
 */
export type BearerRenewal =
  | { kind: 'renewed'; token: string }
  /** The membership is gone. Nothing this client asks will ever be authorized again. */
  | { kind: 'refused' }
  /** Undecided. Keep the bearer; the next request may try again. */
  | { kind: 'unavailable' };

/**
 * A bearer token that can be **re-minted from the membership's durable credential**, with the
 * one-at-a-time renewal that replaces it.
 *
 * Shared by the two rounds clients ({@link RoundsHttpClient} and `RoundsHostHttpClient`) rather
 * than written twice: the dedupe is the subtle part — a room's worth of polls all losing the same
 * bearer at the same instant must cost **one** mint, not one each — and two copies of it would be
 * two chances to get it wrong.
 */
export class RenewableBearer {
  private token: string;
  private readonly renewToken: (() => Promise<BearerRenewal>) | undefined;
  private renewal: Promise<string | null> | null = null;
  /** Set once a renewal is definitively refused: this bearer is retired and mints no more. */
  private retired = false;

  constructor(token: string, renewToken?: () => Promise<BearerRenewal>) {
    this.token = token;
    this.renewToken = renewToken;
  }

  /** The token to authorize the next request with. */
  current(): string {
    return this.token;
  }

  /** Whether a renewal is even possible — a client built without one, or one whose membership has
   * been definitively refused, never mints again. */
  get renewable(): boolean {
    return this.renewToken !== undefined && !this.retired;
  }

  /**
   * Renews `attempted`, answering the fresh token or `null` when nothing replaced it.
   *
   * A caller whose `attempted` has already been superseded by a concurrent renewal gets the
   * current token back, having minted nothing.
   */
  renew(attempted: string): Promise<string | null> {
    if (!this.renewable) {
      return Promise.resolve(null);
    }
    if (this.token !== attempted) {
      return Promise.resolve(this.token);
    }
    if (this.renewal !== null) {
      return this.renewal;
    }
    const attempt = (async (): Promise<string | null> => {
      try {
        const outcome = await this.renewToken!();
        if (outcome.kind === 'renewed') {
          this.token = outcome.token;
          return outcome.token;
        }
        if (outcome.kind === 'refused') {
          this.retired = true;
        }
        return null;
      } catch {
        // A throw is undecided, like `unavailable`: an unreachable mint is not a lost membership,
        // so the bearer is kept and the next request may try again — exactly as every other
        // transient failure on these clients degrades.
        return null;
      } finally {
        this.renewal = null;
      }
    })();
    this.renewal = attempt;
    return attempt;
  }
}

function jsonOf(raw: RawResponse): unknown {
  if (raw.bytes.byteLength === 0) {
    return null;
  }
  try {
    return JSON.parse(new TextDecoder().decode(raw.bytes));
  } catch {
    return null;
  }
}

/** Turns a non-2xx trace response (manifest or coarse) into the screen's corruption report. */
function traceRefusalFrom(raw: RawResponse): TraceLoad {
  const body = jsonOf(raw) as Record<string, unknown> | null;
  const error = typeof body?.['error'] === 'string' ? (body['error'] as string) : `http_${raw.status}`;
  const component = body?.['component'];
  return {
    kind: 'corrupt',
    component: (component === 'detail' || component === 'index' ? component : 'coarse') as
      TraceComponentName,
    message:
      typeof body?.['detail'] === 'string' ? `${error}: ${body['detail'] as string}` : error,
  };
}

/** The spans a `410 detail_evicted` body carries, tolerant of a malformed entry (a span
 * that will not parse is simply dropped rather than treated as the whole body malformed —
 * the request already failed 410; the client's own job here is only to learn what it can). */
function evictedSpansFromBody(body: Record<string, unknown> | null): EvictedSpan[] {
  const raw = Array.isArray(body?.['spans']) ? (body['spans'] as unknown[]) : [];
  const spans: EvictedSpan[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') {
      continue;
    }
    const row = entry as Record<string, unknown>;
    if (typeof row['fromTick'] === 'number' && typeof row['toTick'] === 'number') {
      spans.push({ fromTick: row['fromTick'], toTick: row['toTick'] });
    }
  }
  return spans;
}

/**
 * Classifies a **non-2xx detail-range** response, which is the whole of
 * implementation-review **F9** extended by `rounds-trace-retention`: `trace_corrupt` and
 * `trace_absent` are `rounds-trace`'s typed statements that the declared bytes are not
 * there to be read and are never benign; `410 detail_evicted` is a **third**, distinct
 * outcome — the bytes were deliberately reclaimed, not damaged — handled by the caller
 * before this function is even asked; everything else (a 503, a bare 5xx, a
 * `retention_unreadable` the server itself could not classify, a proxy's HTML error page)
 * is a transport failure a later scrub may well succeed at.
 *
 * Returning `null` means "transient": keep showing coarse, stay retryable, say nothing.
 */
function detailCorruptionFrom(
  status: number,
  body: Record<string, unknown> | null,
): TraceCorruptionReport | null {
  const error = typeof body?.['error'] === 'string' ? (body['error'] as string) : null;
  if (error !== 'trace_corrupt' && error !== 'trace_absent') {
    return null;
  }
  const declared = body?.['component'];
  const component: TraceComponentName =
    declared === 'detail' || declared === 'index' || declared === 'coarse'
      ? declared
      : 'detail';
  const detail = typeof body?.['detail'] === 'string' ? (body['detail'] as string) : null;
  return {
    component,
    message: detail === null ? `${error} (${status})` : `${error}: ${detail}`,
  };
}

/** The URLs one trace's manifest/coarse/detail routes live at — the own-sandbox family's
 * `/rounds/{sandbox}/runs/{runId}/trace/…` and the tournament-read family's
 * `/rounds/tournaments/{t}/rounds/{n}/colonies/{c}/trace/…` both satisfy this, so the trace-
 * opening logic below is written once and addressed twice. */
interface TraceRoutes {
  manifestUrl: string;
  coarseUrl: string;
  detailUrl(fromTick: number, toTick: number): string;
}

/**
 * Opens a trace over `routes`, using `send` for every request. Shared by both clients: the
 * manifest envelope is parsed through {@link parseTraceManifestResponse} (so a retention
 * state that will not parse refuses the trace exactly as a manifest missing a declared
 * field does), the coarse tier is fail-closed on its declared length, and the returned
 * `ReviewTrace`'s detail residency arrives by window exactly as `traceOver` always has.
 */
async function openTraceOver(
  send: (method: string, url: string) => Promise<RawResponse>,
  routes: TraceRoutes,
  detailWindowTicks: number,
  maxResidentDetailWindows: number,
): Promise<TraceLoad> {
  let manifest: TraceManifest;
  let retention: TraceRetention;
  let tickRateHz: number;
  let perCellCapacity: number;
  let coarse: readonly CoarseSample[];
  let preamble: CoarsePreamble;
  try {
    const manifestRaw = await send('GET', routes.manifestUrl);
    if (!manifestRaw.ok) {
      return traceRefusalFrom(manifestRaw);
    }
    const parsed = parseTraceManifestResponse(jsonOf(manifestRaw));
    manifest = parsed.manifest;
    retention = parsed.retention;
    // tick-rate-single-source E5/task 5.3: the rate rides the same envelope as the manifest and
    // the retention state — the run's own recorded rate, never a client-side default. The pool
    // ceiling's multiplier (WAG-100) rides beside it, read off the same recorded balance.
    tickRateHz = parsed.tickRateHz;
    perCellCapacity = parsed.perCellCapacity;

    const coarseRaw = await send('GET', routes.coarseUrl);
    if (!coarseRaw.ok) {
      return traceRefusalFrom(coarseRaw);
    }
    // Fail-closed on the declared length, exactly as `openTrace` does for a local file:
    // a short coarse tier is corruption, not a partial view.
    if (coarseRaw.bytes.byteLength !== manifest.components.coarse.byteLen) {
      throw new TraceCorruptionError(
        'coarse',
        `declared ${manifest.components.coarse.byteLen} byte(s), got ${coarseRaw.bytes.byteLength}`,
      );
    }
    // The coarse tier's leading preamble is the patch table every detail record's patch section
    // is read against — decoded here, once, off the same bytes the coarse tier was fetched for,
    // rather than a second request for a component the trace does not have.
    const decoded = decodeCoarse(coarseRaw.bytes);
    preamble = decoded.preamble;
    coarse = decoded.samples;
  } catch (error) {
    if (error instanceof TraceCorruptionError) {
      return { kind: 'corrupt', component: error.component, message: error.message };
    }
    return {
      kind: 'corrupt',
      component: 'coarse',
      message: String((error as Error)?.message ?? error),
    };
  }
  return {
    kind: 'loaded',
    trace: buildReviewTrace(
      send,
      routes,
      manifest,
      retention,
      tickRateHz,
      perCellCapacity,
      coarse,
      preamble,
      detailWindowTicks,
      maxResidentDetailWindows,
    ),
  };
}

/**
 * The `ReviewTrace` over a manifest + the whole coarse tier, with detail arriving by
 * window. `requestDetail` is fire-and-forget and idempotent per window: a scrub that
 * lands repeatedly inside one in-flight window issues exactly one fetch.
 *
 * Three failure modes are kept apart (**F9**, extended by `rounds-trace-retention`):
 *
 *   * a *transient* failure leaves the window un-requested so a later scrub retries it,
 *     and the screen keeps rendering coarse;
 *   * a **typed evicted refusal** — known upfront from the manifest's own retention
 *     state, or learned from a `410 detail_evicted` response — marks the covering span
 *     evicted and the window is never asked for again; no failure is surfaced;
 *   * **typed corruption** latches the whole trace corrupt and is published through
 *     `onCorrupt`, because coarse is a legitimate view and a damaged trace must never be
 *     able to masquerade as one (**D4**, **D6**).
 */
function buildReviewTrace(
  send: (method: string, url: string) => Promise<RawResponse>,
  routes: TraceRoutes,
  manifest: TraceManifest,
  retention: TraceRetention,
  tickRateHz: number,
  perCellCapacity: number,
  coarse: readonly CoarseSample[],
  preamble: CoarsePreamble,
  detailWindowTicks: number,
  maxResidentDetailWindows: number,
): ReviewTrace {
  const resident = new Map<number, DetailRecord>();
  const requested = new Set<number>();
  /**
   * FIFO bookkeeping for `resident` (E2E round-3 finding D): which ticks each successfully
   * fetched window wrote, in fetch-completion order. `Map` iteration order is insertion
   * order, so the first key is always the oldest still-resident window — the one
   * {@link evictOldestWindowsIfOverCap} reclaims first once more than
   * `maxResidentDetailWindows` are held. Without this, `resident` and `requested` only ever
   * grew: a sustained fast-forward over a long trace decodes and keeps every window it
   * passes for the rest of the trace's lifetime (see {@link DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS}).
   */
  const windowTicks = new Map<number, number[]>();
  /** Evicts the least-recently-fetched window(s) until at most `maxResidentDetailWindows`
   * remain resident. Also un-marks the evicted window from `requested`, so a later scrub
   * back into it re-fetches rather than being stuck reading coarse forever — the bytes were
   * never evicted server-side, only this client's own decoded copy of them. */
  const evictOldestWindowsIfOverCap = (): void => {
    while (windowTicks.size > maxResidentDetailWindows) {
      const oldest = windowTicks.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      for (const tick of windowTicks.get(oldest)!) {
        resident.delete(tick);
      }
      windowTicks.delete(oldest);
      requested.delete(oldest);
    }
  };
  // The fetch window starts at a real anchor and covers at least one whole keyframe cadence.
  //
  // The server extends a byte range back to the keyframe governing it, so a mis-aligned request
  // is answered with a decodable body rather than an undecodable one — but asking for an aligned
  // window is what makes that reach-back *empty*, and an empty reach-back is the whole reason the
  // cadence was matched to this client's own fetch stride. Aligning here is therefore not
  // defensive duplication: it is the client keeping its side of the arrangement that makes a scrub
  // read exactly the bytes it needs and no others. Widening rather than hardcoding
  // `detailWindowTicks === keyframeCadenceTicks` is what keeps this correct if a future
  // measurement moves the cadence without moving this client's own prefetch width.
  const { firstTick, terminalTick, keyframeCadenceTicks } = manifest;
  const window = Math.max(1, detailWindowTicks, keyframeCadenceTicks);

  /** The one canonical evicted-spans signal both the indicator and the track read.
   * Starts from the manifest's own retention state and grows if a detail fetch ever
   * learns of an eviction the manifest did not yet report (a sweep racing the read). */
  let evictedSpans = normalizeEvictedSpans(retention.detail.evictedSpans, firstTick, terminalTick);
  const isEvicted = (tick: number): boolean =>
    evictedSpans.some((span) => tick >= span.fromTick && tick <= span.toTick);

  /** The one corruption report this trace will ever carry, once seen. */
  let corruption: TraceCorruptionReport | null = null;
  const corruptListeners = new Set<(report: TraceCorruptionReport) => void>();
  const reportCorrupt = (report: TraceCorruptionReport): void => {
    if (corruption !== null) {
      return; // first report wins; a corrupt trace does not get worse
    }
    corruption = report;
    for (const listener of corruptListeners) {
      listener(report);
    }
  };

  // The window's start is the tick's governing **keyframe anchor** — never the client's own
  // `detailWindowTicks` stride, which the manifest's cadence need not equal. This is what
  // guarantees the raw bytes `fetchWindow` receives always begin with a keyframe.
  const windowStart = (tick: number): number =>
    spanAnchor(Math.max(firstTick, tick), firstTick, keyframeCadenceTicks);

  /** Thrown for a transient failure, so `requestDetail`'s catch re-arms the window. */
  class DetailUnavailable extends Error {}

  const fetchWindow = async (start: number): Promise<void> => {
    const end = Math.min(terminalTick, start + window - 1);
    const raw = await send('GET', routes.detailUrl(start, end));
    if (!raw.ok) {
      const body = jsonOf(raw) as Record<string, unknown> | null;
      if (raw.status === 410 && body?.['error'] === 'detail_evicted') {
        // Not corruption and not transient: the bytes are gone on purpose. Learn the
        // span(s) the server named so this window — and any other tick inside them —
        // is never asked for again.
        evictedSpans = normalizeEvictedSpans(
          [...evictedSpans, ...evictedSpansFromBody(body)],
          firstTick,
          terminalTick,
        );
        return;
      }
      const typed = detailCorruptionFrom(raw.status, body);
      if (typed !== null) {
        reportCorrupt(typed);
        return;
      }
      // Transient: the window stays fetchable and the screen stays on coarse.
      throw new DetailUnavailable(`the server answered ${raw.status}`);
    }
    let rest = raw.bytes;
    // A fresh replayer per response: `start` is always a keyframe anchor (see `windowStart`),
    // so the first record this feeds is always self-contained and every record after it in the
    // same response is a delta against the one before — exactly the span `SpanReplayer` expects.
    const replayer = new SpanReplayer(preamble);
    const ticksWritten: number[] = [];
    while (rest.byteLength > 0) {
      const framed = unframe(rest);
      if (framed === null) {
        // A 2xx body that is not framed detail is damage, not latency: the bytes the
        // manifest declares are not the bytes that arrived.
        reportCorrupt({
          component: 'detail',
          message: `a detail record frame in ticks ${start}..${end} is truncated`,
        });
        return;
      }
      let record: DetailRecord;
      try {
        record = replayer.feed(framed.payload);
      } catch (error) {
        reportCorrupt({
          component: error instanceof TraceCorruptionError ? error.component : 'detail',
          message: String((error as Error)?.message ?? error),
        });
        return;
      }
      resident.set(record.tick, record);
      ticksWritten.push(record.tick);
      rest = rest.subarray(framed.framedLength);
    }
    if (ticksWritten.length > 0) {
      windowTicks.set(start, ticksWritten);
      evictOldestWindowsIfOverCap();
    }
  };

  return {
    firstTick,
    terminalTick,
    tickRateHz,
    perCellCapacity,
    fieldSize: manifest.denominators.fieldSize,
    coarseAt(tick: number): CoarseSample | null {
      let found: CoarseSample | null = null;
      for (const sample of coarse) {
        if (sample.tick <= tick) {
          found = sample;
        } else {
          break;
        }
      }
      return found;
    },
    detailAt(tick: number): DetailRecord | null {
      // Fail closed: a corrupt trace serves no frames at all, so nothing downstream can
      // render half a run as though it were whole.
      return corruption === null ? resident.get(tick) ?? null : null;
    },
    requestDetail: (tick: number): void => {
      if (corruption !== null || tick < firstTick || tick > terminalTick) {
        return;
      }
      if (isEvicted(tick)) {
        // The range can never arrive: asking would retry forever or eventually surface
        // as a failure, and this is not one — the server did exactly what it was
        // configured to do.
        return;
      }
      const start = windowStart(tick);
      if (requested.has(start)) {
        return;
      }
      requested.add(start);
      void fetchWindow(start).catch(() => {
        // A transport failure only: allow a retry, and stay on coarse until then. Typed
        // corruption and a typed eviction never reach here — both resolved inside
        // `fetchWindow` instead of throwing.
        requested.delete(start);
      });
    },
    get evictedSpans(): readonly EvictedSpan[] {
      return evictedSpans;
    },
    onCorrupt: (listener: (report: TraceCorruptionReport) => void): (() => void) => {
      if (corruption !== null) {
        listener(corruption);
      }
      corruptListeners.add(listener);
      return () => {
        corruptListeners.delete(listener);
      };
    },
  };
}

/**
 * The `rounds-http` client. One instance is scoped to one sandbox and one credential —
 * there is deliberately no way to point it at another player's scope after construction.
 */
export class RoundsHttpClient implements ColonyRunController {
  private readonly baseUrl: string;
  private readonly scope: string;
  /** Renewable, because a bearer is the perishable half of a membership: see
   * {@link RoundsHttpConfig.renewToken}. */
  private readonly bearer: RenewableBearer;
  private readonly fetchImpl: typeof fetch;
  private readonly detailWindowTicks: number;
  private readonly maxResidentDetailWindows: number;

  constructor(config: RoundsHttpConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.scope = config.scope;
    this.bearer = new RenewableBearer(config.token, config.renewToken);
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.detailWindowTicks = Math.max(1, config.detailWindowTicks ?? DEFAULT_DETAIL_WINDOW_TICKS);
    this.maxResidentDetailWindows = Math.max(
      1,
      config.maxResidentDetailWindows ?? DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS,
    );
  }

  /** The sandbox-scoped path prefix every route hangs off. */
  private path(suffix: string): string {
    return `${this.baseUrl}/rounds/${this.scope}${suffix}`;
  }

  /**
   * One bearer-authorized request, **renewing the bearer once** if the server disowns it.
   *
   * Here rather than at any caller, deliberately: every route on this client — the phase poll, the
   * standings read, the colony seed, Run, the version surface, the trace tiers, the chat client it
   * hands out — goes through this one method, so a recovery written at the call sites would have to
   * be written a dozen times and would still miss the next route added.
   *
   * The retry is **once**, and only for the refusal that names a bearer the server does not know.
   * A refusal that survives a fresh bearer is a real refusal and is returned as one.
   */
  private async send(method: string, url: string, body?: unknown): Promise<RawResponse> {
    const attempted = this.bearer.current();
    const raw = await sendRequest(this.fetchImpl, attempted, method, url, body);
    if (!this.bearer.renewable || !bearerDisowned(raw)) {
      return raw;
    }
    const fresh = await this.bearer.renew(attempted);
    if (fresh === null) {
      return raw;
    }
    return sendRequest(this.fetchImpl, fresh, method, url, body);
  }

  // --- ColonyRunController ---------------------------------------------------------

  async submit(candidate: RunCandidate): Promise<RunSubmission> {
    let raw: RawResponse;
    try {
      // Design D8: the document itself, plus the ordinal it was edited from — never a per-type
      // reassembly, and never a birth type named separately (the document declares it).
      raw = await this.send('POST', this.path('/runs'), {
        document: candidate.document,
        baseOrdinal: candidate.baseOrdinal,
      });
    } catch (error) {
      return { kind: 'refused', refusal: unavailable(String((error as Error)?.message ?? error)) };
    }
    const body = jsonOf(raw);
    if (!raw.ok) {
      return { kind: 'refused', refusal: refusalFrom(raw.status, body) };
    }
    const runId = (body as Record<string, unknown> | null)?.['runId'];
    if (typeof runId !== 'number') {
      return {
        kind: 'refused',
        refusal: unavailable('the server accepted the run but named no run id'),
      };
    }
    // The run's own committed version, carried on the accept response rather than fetched
    // afterwards — see `RunSubmission`'s doc comment for why a separate read is unsound.
    const documentVersion = (body as Record<string, unknown> | null)?.['documentVersion'];
    if (typeof documentVersion !== 'number') {
      return {
        kind: 'refused',
        refusal: unavailable('the server accepted the run but named no document version'),
      };
    }
    return { kind: 'accepted', runId, documentVersion };
  }

  /**
   * Awaits the run's **single** completion signal. The route signals rather than polls —
   * a batch run has no tick rate to poll at — so this is one long-lived request, and a
   * connection lost across the completion is recovered by {@link listRuns} afterwards.
   */
  async awaitCompletion(runId: number): Promise<RunOutcome> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path(`/runs/${runId}/completion`));
    } catch (error) {
      return {
        kind: 'failed',
        runId,
        status: 'unavailable',
        detail: String((error as Error)?.message ?? error),
      };
    }
    const body = jsonOf(raw);
    if (!raw.ok) {
      return { kind: 'failed', runId, status: `http_${raw.status}` };
    }
    return RoundsHttpClient.outcomeFrom(runId, body as Record<string, unknown> | null);
  }

  private static outcomeFrom(runId: number, dto: Record<string, unknown> | null): RunOutcome {
    const status = typeof dto?.['status'] === 'string' ? (dto['status'] as string) : 'unknown';
    const score = dto?.['score'];
    const terminalTick = dto?.['terminalTick'];
    if (status === 'completed' && typeof score === 'number' && typeof terminalTick === 'number') {
      return { kind: 'completed', runId, score, terminalTick };
    }
    const detail = typeof dto?.['detail'] === 'string' ? (dto['detail'] as string) : undefined;
    return { kind: 'failed', runId, status, detail };
  }

  /** This sandbox's own runs, newest state included. Never a rival's (**G8** is
   * `rounds-trace-http`'s). An unreachable server yields an empty list rather than throwing:
   * the run history is a rail on a screen, not something a boot may die on. */
  async listRuns(): Promise<RunDto[]> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path('/runs'));
    } catch {
      return [];
    }
    const body = jsonOf(raw) as { runs?: unknown } | null;
    if (!raw.ok || !Array.isArray(body?.runs)) {
      return [];
    }
    return body.runs as RunDto[];
  }

  // --- the tournament-read family ----------------------------------------------------
  //
  // Tournament facts fetched with the **session** token this client already holds (**D2**). The
  // path is absolute rather than sandbox-scoped: these routes are about the tournament, not
  // about one member's sandbox.

  /**
   * `GET /rounds/tournament` — the whole phase surface in one read.
   *
   * `null` on any failure, so the phase channel keeps its last known reading rather than
   * inventing a phase: an unreadable surface is not a cancelled tournament, and
   * `client-rounds-phase` requires a cancellation to be *reported* rather than inferred from a
   * failed request.
   */
  async fetchTournament(): Promise<TournamentReadDto | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', `${this.baseUrl}/rounds/tournament`);
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    return tournamentReadFromDto(jsonOf(raw));
  }

  /**
   * `GET /rounds/tournament/standings` — the whole board, readable by every member in every
   * phase. `null` on any failure, which the standings source turns into a **named** unavailable
   * state rather than an empty leaderboard.
   */
  async fetchStandings(): Promise<StandingsDto | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', `${this.baseUrl}/rounds/tournament/standings`);
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    const body = jsonOf(raw);
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
      return null;
    }
    return body as StandingsDto;
  }

  /**
   * `POST /rounds/tournament/selection` — marks `candidateId` as the caller's submission for the
   * current round.
   *
   * **The body is `{candidateId}` and nothing else.** The route is `deny_unknown_fields`, but the
   * reason this matters is upstream of that: **G7** exists so a graded set is one that was
   * *actually simulated together*, so a candidate is named by the identity its Run minted and
   * never re-assembled from the editor's current contents. There is no program source here, no
   * client-side per-type version, and no birth type.
   *
   * A refusal is a **value**, matching every other write on this client, so the affordance maps
   * it to copy rather than catching.
   */
  async markSelection(candidateId: number): Promise<SelectionOutcome> {
    let raw: RawResponse;
    try {
      raw = await this.send('POST', `${this.baseUrl}/rounds/tournament/selection`, {
        candidateId,
      });
    } catch {
      return { kind: 'refused', reason: 'unavailable' };
    }
    const body = jsonOf(raw);
    if (!raw.ok) {
      return { kind: 'refused', reason: selectionRefusalFrom(raw.status, body) };
    }
    const named = (body ?? {}) as Record<string, unknown>;
    const candidateRef = named['candidateRef'];
    if (typeof candidateRef !== 'string') {
      // A 200 that names no submission is not a recorded selection: reporting one would tell a
      // player their score for the round is decided when the server never said so.
      return { kind: 'refused', reason: 'unavailable' };
    }
    return {
      kind: 'recorded',
      provenance: typeof named['provenance'] === 'string' ? named['provenance'] : 'explicit',
      candidateRef,
    };
  }

  // --- the colony program set (the editor's seed) -----------------------------------

  /**
   * The sandbox's **current** whole-colony program set — what the Colony screen renders and what
   * a Run candidate is edited from (implementation-review **F1**).
   *
   * In live mode the roster arrives as a `ColonyMeta` server message over the socket; a rounds
   * deployment mounts no socket, so this route is the *only* way the editor is ever populated.
   * `null` on any failure, so the composition root can say "the colony could not be read" rather
   * than render an empty roster that looks like a colony with no bee types.
   */
  async fetchColony(): Promise<ColonyMeta | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path('/colony'));
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    return colonyMetaFromDto(jsonOf(raw));
  }

  // --- the Beekeeper half of the loop ------------------------------------------------
  //
  // implementation-review **F3**, client half. The server routes exist and are authorized; these
  // two methods are the only way a browser reaches them, because Rounds mode mounts no socket and
  // the live prompt path IS the socket.

  /**
   * Submits a Beekeeper prompt for one bee type.
   *
   * Refusals are **values, not exceptions**, and are mapped onto the same {@link RejectReason}
   * vocabulary the live socket's `Rejected` carries, so the Colony screen turns a rounds refusal
   * into a sentence through exactly the code path it already uses for a live one. No new wire
   * vocabulary, and no protocol bump: this is HTTP mapped onto an existing client type.
   */
  async sendPrompt(typeName: string, promptText: string): Promise<PromptDispatch> {
    let raw: RawResponse;
    try {
      raw = await this.send('POST', this.path(`/types/${encodeURIComponent(typeName)}/prompt`), {
        prompt: promptText,
      });
    } catch {
      // A transport failure: "that didn't go through — try again" is the honest reading, and it
      // leaves the box usable rather than stranded.
      return { kind: 'refused', reason: { kind: 'invalid' } };
    }
    const body = jsonOf(raw) as Record<string, unknown> | null;
    if (!raw.ok) {
      return { kind: 'refused', reason: promptRefusalFrom(raw.status, body) };
    }
    const promptId = typeof body?.['promptId'] === 'number' ? (body['promptId'] as number) : 0;
    const fromSeq = typeof body?.['fromSeq'] === 'number' ? (body['fromSeq'] as number) : 0;
    return { kind: 'queued', promptId, fromSeq };
  }

  /**
   * Reads this sandbox's generation statuses after `afterSeq`. The route **long-polls** (a status
   * is pushed, not discovered), so this is one long-lived request per generation step rather than
   * one per second.
   *
   * `null` means "ask again": a transport failure or a non-2xx answer is never a status, and a
   * status log is not something a boot may die on.
   */
  async pollPromptStatus(afterSeq: number): Promise<PromptStatusPage | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path(`/prompts/status?afterSeq=${afterSeq}`));
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    return promptStatusPageFrom(jsonOf(raw), afterSeq);
  }

  /**
   * Hand-edits one type's verbatim slice of the rounds working document
   * (`POST /rounds/{sandbox}/types/{typeName}/source`, CONTRACT.md §6) — the Code tab's per-type
   * Save in `GameMode::Rounds` (client-colony "Read-only code view, editable via HandEdit").
   * `baseVersion` is the document's own ordinal, `null` when none has been observed yet
   * (`ui/colony/controller.ts`'s `UNKNOWN_DOCUMENT_BASE_VERSION` sentinel is `rounds-prompt.ts`'s
   * concern to translate, not this client's).
   */
  async handEditType(typeName: string, source: string, baseVersion: number | null): Promise<HandEditDispatch> {
    return this.postHandEdit(this.path(`/types/${encodeURIComponent(typeName)}/source`), source, baseVersion);
  }

  /**
   * Hand-edits the whole rounds working document (`POST /rounds/{sandbox}/document`,
   * CONTRACT.md §6) — the Code tab's degraded whole-document view's Save, a player's only way to
   * repair a header that no longer parses (client-colony "A non-parsing document degrades to the
   * whole-document view").
   */
  async handEditDocument(source: string, baseVersion: number | null): Promise<HandEditDispatch> {
    return this.postHandEdit(this.path('/document'), source, baseVersion);
  }

  /** Shared by {@link handEditType} and {@link handEditDocument}: identical request/response shape
   * (CONTRACT.md §6), differing only in which route carries it. A transport failure and a non-2xx
   * answer both become a value, never a throw — the same "refusals are values" discipline
   * {@link sendPrompt} already keeps. */
  private async postHandEdit(
    url: string,
    source: string,
    baseVersion: number | null,
  ): Promise<HandEditDispatch> {
    let raw: RawResponse;
    try {
      raw = await this.send('POST', url, { source, baseVersion });
    } catch {
      return { kind: 'refused', reason: { kind: 'invalid' } };
    }
    const body = jsonOf(raw);
    if (!raw.ok) {
      return { kind: 'refused', reason: handEditRefusalFrom(raw.status, body as Record<string, unknown> | null) };
    }
    return { kind: 'applied', result: handEditResultFrom(body) };
  }

  // --- the document version surface (sandbox-screen-consolidation) -------------------

  /**
   * This sandbox's **document version history**, oldest first, plus the head named outright.
   *
   * **Never throws, and never rejects.** A version selector is a control on a screen, not
   * something a mount may die on: a refusal, a garbled body or a dead transport all read as
   * `{ headOrdinal: 0, versions: [] }`, an empty history whose head is an ordinal no sandbox ever
   * issues — so "nothing was read" can never be mistaken for "version zero is current".
   *
   * The rows carry no document text by design (see {@link DocumentVersionDto}); the bytes arrive
   * with {@link activateVersion}, which is the only moment they are needed.
   */
  async listVersions(): Promise<VersionListDto> {
    const empty: VersionListDto = { headOrdinal: 0, versions: [] };
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path('/versions'));
    } catch {
      return empty;
    }
    if (!raw.ok) {
      return empty;
    }
    const body = jsonOf(raw) as Record<string, unknown> | null;
    if (typeof body?.['headOrdinal'] !== 'number' || !Array.isArray(body['versions'])) {
      return empty;
    }
    const versions: DocumentVersionDto[] = [];
    for (const entry of body['versions']) {
      const version = documentVersionFrom(entry);
      if (version !== null) {
        versions.push(version);
      }
    }
    return { headOrdinal: body['headOrdinal'], versions };
  }

  /**
   * Restores one version's **whole document** — every type's program and the birth type together,
   * because a whole-document snapshot is the unit a version is.
   *
   * The server answers in the hand-edit routes' own body, so this reuses `handEditResultFrom`
   * rather than parsing the same five fields a second way: whichever of the three routes moved
   * the document, the caller applies one result through one state transition.
   *
   * Activation **relocates the head** — it allocates no ordinal — so the `version` in the result
   * is the restored ordinal, not a new one, and the next save still allocates above every ordinal
   * ever issued.
   */
  async activateVersion(ordinal: number): Promise<ActivateDispatch> {
    let raw: RawResponse;
    try {
      raw = await this.send('POST', this.path(`/versions/${ordinal}/activate`));
    } catch (error) {
      return { kind: 'failed', detail: String((error as Error)?.message ?? error) };
    }
    const body = jsonOf(raw);
    if (!raw.ok) {
      return activateRefusalFrom(raw.status, body as Record<string, unknown> | null);
    }
    return { kind: 'applied', result: handEditResultFrom(body) };
  }

  // --- the review rail's run history -------------------------------------------------

  /** A published trace's manifest envelope — the manifest plus its recorded tick rate
   * (tick-rate-single-source **E5**) — or `null` when there is none to read (a cancelled, failed
   * or still-running run answers `404 trace_absent`, which is a real state, not a fault) — or when
   * the response's retention state will not parse, which is refused exactly the same way rather
   * than opened as "retained" (`rounds-trace-http`, architecture-review **F3**). */
  async fetchTraceManifest(runId: number | string): Promise<TraceManifestResponse | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path(`/runs/${runId}/trace/manifest`));
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    try {
      return parseTraceManifestResponse(jsonOf(raw));
    } catch {
      return null;
    }
  }

  /**
   * This sandbox's **reviewable** runs, in the order they were produced, as the review screen's
   * rail consumes them (implementation-review **F1**).
   *
   * Only runs with a published trace can be reviewed, so a run without one is not listed — a rail
   * entry that cannot be opened would be a dead control. Each entry's seed comes from that run's
   * own manifest, because the run list does not carry one and a placeholder seed on a screen whose
   * entire purpose is stating what you are looking at would be worse than a second small fetch.
   */
  async listReviewRuns(): Promise<ReviewRun[]> {
    const runs = await this.listRuns();
    const numbered = runs
      .slice()
      .sort((a, b) => a.runId - b.runId)
      .map((dto, index) => ({ dto, number: index + 1 }));
    const reviewable = await Promise.all(
      numbered
        .filter(({ dto }) => dto.status === 'completed')
        .map(async ({ dto, number }) => {
          const envelope = await this.fetchTraceManifest(dto.runId);
          return envelope === null ? null : reviewRunFrom(dto, number, envelope.manifest, envelope.tickRateHz);
        }),
    );
    return reviewable.filter((run): run is ReviewRun => run !== null);
  }

  // --- TraceReader -----------------------------------------------------------------

  /**
   * A `TraceReader` over this sandbox's trace routes, for the review screen.
   *
   * Understands only the locator's `sandboxRun` arm — the only address this scope's routes
   * ever answer. A `gradedRound` locator reaching this reader would be a caller bug rather
   * than a runtime condition: the composition root's dispatching reader (**D3**,
   * `createDispatchingTraceReader`) routes that arm to `TournamentReadClient` instead, so this
   * branch is never exercised in production.
   */
  createTraceReader(): TraceReader {
    return {
      open: (locator: TraceLocator): Promise<TraceLoad> => {
        if (locator.kind === 'sandboxRun') {
          return this.openTrace(locator.runId);
        }
        if (locator.kind === 'tournamentPreview') {
          return this.openPreview();
        }
        return Promise.reject(
          new Error('RoundsHttpClient cannot open a graded-round locator; it has no such route'),
        );
      },
    };
  }

  // --- the chat transport (beekeeper-agent-chat, CONTRACT.md §6/§7) -----------------

  /** A `ChatSseClient` scoped to this same sandbox and credential, for the chat overlay's channel
   * (`app/rounds/chat-channel.ts`). Mirrors `createTraceReader()`: one client per sandbox, built
   * from the same `baseUrl`/`scope`/`token`/`fetchImpl` this instance already carries.
   *
   * The bearer is read **at call time**, so a chat client opened after a renewal carries the
   * renewed token rather than the one the server has already forgotten. */
  createChatClient(): ChatSseClient {
    return new ChatSseClient({
      baseUrl: this.baseUrl,
      scope: this.scope,
      token: this.bearer.current(),
      fetchImpl: this.fetchImpl,
    });
  }

  /** The sandbox arm's own address: the numeric run id `ColonyRunController` already carries
   * (**D3**) — no `String()` conversion here or at any caller since the locator widening. */
  private openTrace(runId: number): Promise<TraceLoad> {
    const routes: TraceRoutes = {
      manifestUrl: this.path(`/runs/${runId}/trace/manifest`),
      coarseUrl: this.path(`/runs/${runId}/trace/coarse`),
      detailUrl: (fromTick, toTick) =>
        this.path(`/runs/${runId}/trace/detail?fromTick=${fromTick}&toTick=${toTick}`),
    };
    return openTraceOver(
      (method, url) => this.send(method, url),
      routes,
      this.detailWindowTicks,
      this.maxResidentDetailWindows,
    );
  }

  /**
   * `GET /rounds/tournament/preview/{manifest,coarse,detail}` (`rounds-preview-run`) — the
   * tournament's shared preview trace, addressed by nothing but the caller's own bearer. No run
   * id appears anywhere on this path, matching the route: the preview is nobody's run.
   */
  private openPreview(): Promise<TraceLoad> {
    const routes: TraceRoutes = {
      manifestUrl: `${this.baseUrl}/rounds/tournament/preview/manifest`,
      coarseUrl: `${this.baseUrl}/rounds/tournament/preview/coarse`,
      detailUrl: (fromTick, toTick) =>
        `${this.baseUrl}/rounds/tournament/preview/detail?fromTick=${fromTick}&toTick=${toTick}`,
    };
    return openTraceOver(
      (method, url) => this.send(method, url),
      routes,
      this.detailWindowTicks,
      this.maxResidentDetailWindows,
    );
  }
}

export interface TournamentReadConfig {
  /** e.g. `http://127.0.0.1:8080` — no trailing slash required. */
  baseUrl: string;
  /** The tournament this credential is a member of. */
  tournamentId: string;
  /**
   * The bearer credential a tournament membership mint returned, hex. Distinct from a
   * sandbox `SessionToken`: the server's own gate parses the two by length (32 hex chars
   * for a session token, 64 for a tournament credential — `rounds-tournament-trace-read`),
   * so neither family's credential is ever mistaken for the other's.
   */
  credential: string;
  fetchImpl?: typeof fetch;
  /** See {@link RoundsHttpConfig.detailWindowTicks}. */
  detailWindowTicks?: number;
  /** See {@link DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS}. */
  maxResidentDetailWindows?: number;
}

/**
 * The **read-only** client for the tournament-read route family
 * (`rounds-tournament-trace-read`, **G8** / **A8**): every tournament member's graded-round
 * trace, addressed by `(tournament, round, colony)` — **never** by run id, which is what
 * makes a sandbox run inexpressible through this family rather than merely filtered out
 * of it (**H4**). One instance is scoped to one tournament and one credential, the same
 * construction-time binding {@link RoundsHttpClient} uses for a sandbox.
 *
 * Deliberately narrower than `RoundsHttpClient`: there is no `submit`, `cancel`,
 * `sendPrompt`, `pollPromptStatus` or any other mutating or program-source method,
 * because the route family this addresses mounts none — the server's own
 * `.../program` route answers `unauthorized` unconditionally for every colony including
 * the caller's own (**H5**), so a client method for it could only ever fail and would
 * teach nothing a refusal from the trace routes does not already.
 */
export class TournamentReadClient {
  private readonly baseUrl: string;
  private readonly tournamentId: string;
  private readonly credential: string;
  private readonly fetchImpl: typeof fetch;
  private readonly detailWindowTicks: number;
  private readonly maxResidentDetailWindows: number;

  constructor(config: TournamentReadConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.tournamentId = config.tournamentId;
    this.credential = config.credential;
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.detailWindowTicks = Math.max(1, config.detailWindowTicks ?? DEFAULT_DETAIL_WINDOW_TICKS);
    this.maxResidentDetailWindows = Math.max(
      1,
      config.maxResidentDetailWindows ?? DEFAULT_MAX_RESIDENT_DETAIL_WINDOWS,
    );
  }

  /** The `(tournament, round, colony)`-addressed path prefix every route hangs off. No
   * run id ever appears here — there is no parameter to carry one. */
  private path(round: number, colony: number, suffix: string): string {
    return `${this.baseUrl}/rounds/tournaments/${encodeURIComponent(this.tournamentId)}/rounds/${round}/colonies/${colony}${suffix}`;
  }

  private send(method: string, url: string): Promise<RawResponse> {
    return sendRequest(this.fetchImpl, this.credential, method, url);
  }

  /**
   * The manifest and retention state for a member's graded-round trace, or `null` when it
   * cannot be read — refused (no membership, a foreign tournament, an ungraded round, a
   * colony outside the roster), or a retention state this build cannot parse, refused
   * exactly as `RoundsHttpClient.fetchTraceManifest` refuses one.
   */
  async fetchTraceManifest(round: number, colony: number): Promise<TraceManifestResponse | null> {
    let raw: RawResponse;
    try {
      raw = await this.send('GET', this.path(round, colony, '/trace/manifest'));
    } catch {
      return null;
    }
    if (!raw.ok) {
      return null;
    }
    try {
      return parseTraceManifestResponse(jsonOf(raw));
    } catch {
      return null;
    }
  }

  /**
   * Opens a member's graded-round trace for the review screen, over the same
   * `openTraceOver`/`buildReviewTrace` machinery {@link RoundsHttpClient} uses for its own
   * sandbox — **one** trace-opening implementation, addressed twice, which is exactly what
   * makes "cross-player visibility introduces no second, looser read path"
   * (`rounds-tournament-trace-read`) true on the client as well as the server.
   */
  openTrace(round: number, colony: number): Promise<TraceLoad> {
    const routes: TraceRoutes = {
      manifestUrl: this.path(round, colony, '/trace/manifest'),
      coarseUrl: this.path(round, colony, '/trace/coarse'),
      detailUrl: (fromTick, toTick) =>
        this.path(round, colony, `/trace/detail?fromTick=${fromTick}&toTick=${toTick}`),
    };
    return openTraceOver(
      (method, url) => this.send(method, url),
      routes,
      this.detailWindowTicks,
      this.maxResidentDetailWindows,
    );
  }
}

/**
 * Maps `rounds-http`'s `ColonyDto` onto the client's `ColonyMeta` — the same shape the live
 * socket's `ColonyMeta` message decodes to, so the Colony screen, `captureCandidate` and the
 * version bar all work unchanged in rounds mode (implementation-review **F1**).
 *
 * Two translations are worth naming:
 *
 *   * the DTO's `birthType` is a type **name** (the surface talks names, since ids are internal),
 *     while `ColonyMeta.birthType` is a `typeId` — resolved by name here, falling back to the
 *     first type rather than to a `typeId` no type has;
 *   * `tick` is `0`. A sandbox has no running clock: ticks only exist inside a run, and the
 *     surface's own status events stamp `0` before the first one for exactly this reason.
 *
 * `null` for anything that is not a colony (a non-object body, a missing/ill-typed `types`), so a
 * malformed answer becomes "the colony could not be read" instead of an empty roster.
 */
export function colonyMetaFromDto(json: unknown): ColonyMeta | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    return null;
  }
  const dto = json as Record<string, unknown>;
  const rawTypes = dto['types'];
  if (!Array.isArray(rawTypes)) {
    return null;
  }
  const types: TypeMeta[] = [];
  for (const entry of rawTypes) {
    if (entry === null || typeof entry !== 'object') {
      return null;
    }
    const row = entry as Record<string, unknown>;
    if (
      typeof row['typeId'] !== 'number' ||
      typeof row['name'] !== 'string' ||
      typeof row['source'] !== 'string'
    ) {
      return null;
    }
    types.push({
      typeId: row['typeId'] as number,
      name: row['name'] as string,
      buffName: typeof row['buff'] === 'string' ? (row['buff'] as string) : '',
      activeVersion: typeof row['version'] === 'number' ? (row['version'] as number) : 1,
      canonicalText: row['source'] as string,
    });
  }
  const birthName = typeof dto['birthType'] === 'string' ? (dto['birthType'] as string) : null;
  const birth = types.find((t) => t.name === birthName) ?? types[0];
  return {
    tick: 0,
    types,
    birthType: birth?.typeId ?? 0,
    versionHeads: types.map((t) => ({ typeId: t.typeId, headVersion: t.activeVersion })),
    ...documentFieldsFromDto(dto),
  };
}

/**
 * Parses the Code-tab document additions (CONTRACT.md §6: `document`/`documentValid`/
 * `documentDiagnostics`/`turnInFlight`/`documentVersion`), beekeeper-agent-chat. Every field is
 * independently optional and dropped rather than defaulted when malformed — `colonyMetaFromDto`'s
 * callers already treat an ABSENT document field as "live mode / not this route", so a garbled one
 * must degrade the same way rather than fabricating an empty document that would read as a real
 * (if blank) one.
 *
 * `documentVersion` is read opportunistically: nothing in this file requires `GET /colony` to send
 * it (`rounds-prompt.ts` carries the ordinal forward from a hand-edit save's own response when the
 * read does not have one yet), but a fresh read is always preferred once the surface does.
 */
function documentFieldsFromDto(
  dto: Record<string, unknown>,
): Pick<ColonyMeta, 'document' | 'documentValid' | 'documentDiagnostics' | 'turnInFlight' | 'documentVersion'> {
  const fields: Pick<
    ColonyMeta,
    'document' | 'documentValid' | 'documentDiagnostics' | 'turnInFlight' | 'documentVersion'
  > = {};
  if (typeof dto['document'] === 'string') {
    fields.document = dto['document'];
  }
  if (typeof dto['documentValid'] === 'boolean') {
    fields.documentValid = dto['documentValid'];
  }
  if (typeof dto['turnInFlight'] === 'boolean') {
    fields.turnInFlight = dto['turnInFlight'];
  }
  if (typeof dto['documentVersion'] === 'number') {
    fields.documentVersion = dto['documentVersion'];
  }
  const rawDiagnostics = dto['documentDiagnostics'];
  if (Array.isArray(rawDiagnostics)) {
    fields.documentDiagnostics = documentDiagnosticsFrom(rawDiagnostics);
  }
  return fields;
}

/** Shared by `documentFieldsFromDto` (`GET /colony`) and `handEditResultFrom` (a hand-edit save's
 * own response) — both carry the same `documentDiagnostics` shape. Malformed entries are dropped
 * rather than defaulted, matching every other array parser in this file. */
function documentDiagnosticsFrom(raw: unknown): DocumentDiagnostic[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const diagnostics: DocumentDiagnostic[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') {
      continue;
    }
    const row = entry as Record<string, unknown>;
    if (typeof row['message'] !== 'string' || typeof row['line'] !== 'number' || typeof row['column'] !== 'number') {
      continue;
    }
    diagnostics.push({ message: row['message'], line: row['line'], column: row['column'] });
  }
  return diagnostics;
}

/**
 * Maps one terminal `RunDto` plus its published manifest onto the review screen's `ReviewRun`.
 *
 * Everything comes from the wire or the manifest; nothing is invented. `peakPopulation` is `null`
 * because the surface does not record it — the score readout says "not recorded" rather than
 * quietly showing the final population under a "peak" label, which would be a number that reads
 * as evidence while being about something else.
 *
 * `tickRateHz` is **required** (tick-rate-single-source **E4** / **E5**): the run's own recorded
 * rate, read from the trace-manifest response envelope by the caller, never a client-side
 * default — the exact silent-wrong-answer class the 15→5 Hz experiment on this branch produced
 * (a 30-minute trace once rendering `10:00` because a site divided by a hardcoded 15).
 */
export function reviewRunFrom(
  dto: RunDto,
  number: number,
  manifest: TraceManifest,
  tickRateHz: number,
): ReviewRun {
  const own = dto.evidence?.scores?.[0];
  const livingCount = own?.livingCount ?? dto.score ?? 0;
  return {
    runId: String(dto.runId),
    number,
    kind: 'sandbox',
    seed: manifest.seed,
    colonyCount: dto.evidence?.colonyCount ?? 1,
    terminalTick: dto.terminalTick ?? manifest.terminalTick,
    tickRateHz,
    score: dto.score ?? livingCount,
    stats: {
      peakPopulation: null,
      nectarStored: Math.round(own?.pool ?? 0),
      // A colony with nothing left alive was eliminated (or starved out) at the run's terminal
      // tick; one that finished with bees survived it.
      eliminationTick: livingCount <= 0 ? (dto.terminalTick ?? manifest.terminalTick) : null,
    },
  };
}
